<template lang="pug">
.j-inquiry-table-wrapper(ref="tableWrapperRef" :style="{ 'max-height': tableMaxHeight }")
  v-data-table(
    :headers="internalHeaders"
    :items="items"
    :item-key="itemKey"
    loadingText=""
    fixed-header
    :height="tableMaxHeight"
    disable-pagination
    hide-default-footer
    :items-per-page="-1"
    :class="['j-inquiry-table', tableClass, loading ? `${tableClass}--loading` : '']"
    @click:row="onClickRow"
    :item-class="itemClass"
    :no-data-text="noDataText || $t('common.no_data')"
  )
    template(v-slot:no-data)
      span {{ noDataText || $t('common.no_data') }}
    template(v-for="h in internalHeaders" v-slot:[`header.${h.value}`]="{ header }")
      template(v-if="$scopedSlots[`header-${h.value}`]")
        slot(:name="`header-${h.value}`" :column="header")
      template(v-else)
        div.header-content(
          :class="{ clickable: header.isSortable }"
          @click="header.isSortable && onHeaderClick(header)"
        )
          span {{ header.text }}
          v-icon(
            v-if="header.isSortable"
            color="white"
            :style="{ 'margin-left': '3px' }"
          ) {{ getSortIcon(header.value) }}
    template(v-for="h in internalHeaders" v-slot:[`item.${h.value}`]="{ item }")
      template(v-if="$scopedSlots[`cell-${h.value}`]")
        slot(:name="`cell-${h.value}`" :row="item" :cell="item[h.value]")
      //- 列定義に tooltip: true が指定されている場合、セル値をホバーツールチップで全文表示
      template(v-else-if="h.tooltip && getCellValue(item, h) !== ''")
        v-tooltip(bottom)
          template(v-slot:activator="{ on, attrs }")
            div(:class="getCellClass(item, h)" v-bind="attrs" v-on="on")
              span {{ getCellValue(item, h) }}
          span {{ getCellValue(item, h) }}
      template(v-else)
        div(:class="getCellClass(item, h)")
          span {{ getCellValue(item, h) }}
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  watch,
  getCurrentInstance
} from 'vue'

export default defineComponent({
  name: 'JInquiryTable',
  props: {
    headers: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    itemKey: {
      type: String,
      default: 'id'
    },
    loading: {
      type: Boolean,
      default: false
    },
    tableClass: {
      type: String,
      default: ''
    },
    sortBy: {
      type: String,
      default: null
    },
    itemClass: {
      type: [Function, String],
      default: () => ''
    },
    noDataText: {
      type: String,
      default: ''
    }
  },
  emits: ['row-click', 'update:sortBy'],
  setup(props, { emit }) {
    const proxy = getCurrentInstance()?.proxy
    const $t = (key) => proxy?.$t(key) ?? key

    const tableWrapperRef = ref(null)
    const tableMaxHeight = ref('calc(100vh - 280px)')
    let _bodyObserver = null

    const internalHeaders = computed(() =>
      props.headers.map((col) => ({
        ...col,
        text: col.text != null ? col.text : $t(`table_common.${col.name}`),
        value: col.name,
        align: col.align || 'center',
        minWidth: col.width,
        isSortable: !!col.sortable,
        sortable: false,
        class: col.headerClass
      }))
    )

    const updateTableHeight = () => {
      const el = tableWrapperRef.value
      if (!el) return
      const top = el.getBoundingClientRect().top
      tableMaxHeight.value = `${window.innerHeight - top - 4}px`
    }

    onMounted(() => {
      window.addEventListener('resize', updateTableHeight)
      nextTick(() => updateTableHeight())
      setTimeout(updateTableHeight, 500)
      if (typeof ResizeObserver !== 'undefined') {
        _bodyObserver = new ResizeObserver(updateTableHeight)
        _bodyObserver.observe(document.body)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', updateTableHeight)
      _bodyObserver?.disconnect()
    })

    watch(() => props.items, () => updateTableHeight(), { flush: 'post' })

    const onClickRow = (_item, ctx) => {
      emit('row-click', ctx.index)
    }

    const onHeaderClick = (header) => {
      const tokens = props.sortBy ? props.sortBy.split(',') : []
      const idx = tokens.findIndex(
        (t) => t === header.value || t === `-${header.value}`
      )
      let next
      if (idx === -1) {
        next = [...tokens, header.value]
      } else if (tokens[idx] === header.value) {
        next = [...tokens]
        next[idx] = `-${header.value}`
      } else {
        next = tokens.filter((_, i) => i !== idx)
      }
      emit('update:sortBy', next.length > 0 ? next.join(',') : null)
    }

    const getSortIcon = (headerValue) => {
      const tokens = props.sortBy ? props.sortBy.split(',') : []
      const found = tokens.find(
        (t) => t === headerValue || t === `-${headerValue}`
      )
      if (!found) return 'mdi-sort'
      return found.startsWith('-') ? 'mdi-sort-descending' : 'mdi-sort-ascending'
    }

    const getCellValue = (item, header) => {
      const value = item[header.value]
      if (header.type === 'number') {
        if (value === null || value === undefined || value === '') return ''
        const num = typeof value === 'number' ? value : Number(value)
        if (Number.isNaN(num)) return value
        return num.toLocaleString('ja-JP')
      }
      return value ?? ''
    }

    const getCellClass = (item, header) => {
      const customClass =
        typeof header.cellClass === 'function'
          ? header.cellClass(item, header)
          : header.cellClass || ''
      const classes = [customClass]
      if (header.type === 'number') {
        const value = item[header.value]
        const num = typeof value === 'number' ? value : Number(value)
        if (!Number.isNaN(num) && num < 0) classes.push('red--text')
      }
      return classes.filter(Boolean).join(' ')
    }

    const recalculateHeight = () => {
      nextTick(() => updateTableHeight())
    }

    return {
      tableWrapperRef,
      tableMaxHeight,
      internalHeaders,
      onClickRow,
      onHeaderClick,
      getSortIcon,
      getCellValue,
      getCellClass,
      recalculateHeight
    }
  }
})
</script>

<style lang="sass" scoped>
.j-inquiry-table-wrapper
  width: 100%
  overflow: hidden

.j-inquiry-table
  @import "~@/style/css/vdatatbl.sass"

::v-deep(.v-data-table__wrapper)
  overflow-y: auto
  scroll-padding-top: 36px
  tbody tr td
    white-space: nowrap

.header-content
  display: flex
  align-items: center
  justify-content: center

.clickable
  cursor: pointer
  text-decoration: none
</style>
